import React, { FC, useContext, useState } from "react"
import {
  ContentfulHeading,
  ContentfulLanguageList,
  ContentfulSection,
} from "../../types/graphql-types"
import LocaleContext from "../context/localeContext"
import { useLocation } from "@reach/router"
import { navigate } from "gatsby"
import clsx from "clsx"
import LanguageSelectorElement from "./LanguageSelectorElement"
import BaseModal, { ModalStatus } from "./rootModal"
import { breakpoints as bp, TripleColumnContainer } from "./layout"
import colors from "../utils/colors"
import styled from "styled-components"
import Button from "./forms/button"
import Icon, { flagIconType } from "./Icon"

interface Props {
  contents: ContentfulSection
}

const flagHeight = "24px"
const arrowDownWidth = "24px"

const ColumnContainer = styled(TripleColumnContainer)`
  padding: 0 !important;
  width: 85%;
  margin-top: 33px;
  .isSelected {
    :hover {
      cursor: initial;
    }
    p {
      color: ${colors.teal};
    }
  }
  @media (min-width: ${bp.tabletMin}) {
    margin-top: 96px;
  }

  @media (min-width: ${bp.desktopMin}) {
    width: 80%;
  }
`
const NonDesktopButton = styled(Button)`
  border: none;
  display: flex;
  align-items: center;
  line-height: ${flagHeight};
  text-align: left;
  font-weight: 500;
  padding: 0 calc(1rem + ${arrowDownWidth}) 0 1rem;
  position: relative;
  white-space: nowrap;
  &:hover {
    background: none;
  }
  svg:not(.flag) {
    position: absolute;
    right: 8px;
  }
  @media (min-width: ${bp.tabletMin}) {
    text-align: center;
    width: 100%;
    svg:not(.flag) {
      margin-left: 1rem;
    }
  }
  @media (min-width: ${bp.desktopMin}) {
    display: none;
  }
`
const DesktopButton = styled(Button)`
  display: flex;
  line-height: 20px;
  align-items: center;
  font-size: 18px;
  height: 40px;
  padding: 0 20px;
  margin-right: 1rem;
  white-space: nowrap;
  svg:not(.flag) {
    margin-left: 8px;
  }
  @media (max-width: ${bp.tabletMax}) {
    display: none;
  }
`

const FlagIcon = styled(Icon)`
  height: ${flagHeight};
  width: ${flagHeight};
  margin-right: 1em;

  @media (min-width: ${bp.desktopMin}) {
    margin-right: 0;
  }
`

const LanguageSelector: FC<Props> = props => {
  const { contents } = props
  const locale = useContext(LocaleContext)
  const pagePath = useLocation().pathname
  const pageName = pagePath.endsWith("/")
    ? pagePath
        .split(`${locale}`)
        .pop()
        ?.slice(0, -1)
    : pagePath.split(`${locale}`).pop()
  const heading = (contents?.heading as ContentfulHeading).heading!
  const sectionFields = contents?.contentfulfields
  const selectedLanguage: string =
    (sectionFields as ContentfulLanguageList[])?.find(ele => ele?.localeCode === locale)
      ?.languageName || "English"
  const selectedLocaleCode: string =
    (sectionFields as ContentfulLanguageList[])?.find(ele => ele?.localeCode === locale)
      ?.localeCode || "en"
  const [isLanguageModalOpen, setLanguageModalOpen] = useState(false)
  const redirectLocales = (redirectLocale: string, redirectPath: string) => {
    const resolvedLocale = redirectLocale === "en" ? "" : `/${redirectLocale}`
    const path = redirectPath ? redirectPath : "/"
    return `${resolvedLocale}${path}`
  }
  const selectLanguage = (language: string) => {
    localStorage.setItem("userSelectLanguage", language)
    navigate(redirectLocales(language, pageName!), { replace: true })
  }

  return (
    <>
      <BaseModal
        modalStatus={ModalStatus.CLOSE}
        full
        showToggleIcon
        isOpen={isLanguageModalOpen}
        onClose={() => {
          setLanguageModalOpen(false)
        }}
        header={heading}
      >
        <ColumnContainer>
          {(sectionFields as ContentfulLanguageList[]).map((item, index) => (
            <LanguageSelectorElement
              classNames={clsx(item?.localeCode === locale ? "isSelected" : "")}
              key={index}
              localeName={item.localeName!}
              languageName={item.languageName!}
              ifShowCheckIcon={item?.localeCode === locale}
              onClick={() => {
                item?.localeCode === locale
                  ? setLanguageModalOpen(false)
                  : selectLanguage(item?.localeCode!)
              }}
            />
          ))}
        </ColumnContainer>
      </BaseModal>
      <NonDesktopButton
        type="button"
        transparent
        onClick={() => {
          setLanguageModalOpen(true)
        }}
        data-gtm-cta="language-selector"
      >
        <FlagIcon className="flag" icon={`flag-${selectedLocaleCode}` as flagIconType} />
        {selectedLanguage}
        <Icon icon="arrow-down" />
      </NonDesktopButton>
      <DesktopButton
        type="button"
        transparent
        onClick={() => {
          setLanguageModalOpen(true)
        }}
        data-gtm-cta="language-selector"
      >
        <FlagIcon className="flag" icon={`flag-${selectedLocaleCode}` as flagIconType} />
        <Icon icon="arrow-down" width={arrowDownWidth} height={arrowDownWidth} />
      </DesktopButton>
    </>
  )
}

export default LanguageSelector
